import { type MaybeElementRef } from '@vueuse/core';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type MutationObserverOptions,
} from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';
import type { AxiosError } from 'axios';
import { useI18n } from '@/i18n/useI18n';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import {
  FilterComparator,
  FilterOperator,
  SortDirection,
  type GroupingFilterExpression,
  isComparisonFilterExpression,
  isGroupingFilterExpression,
  type ComparisonFilterExpression,
} from '@/libs/search/types';
import {
  AssetStatus,
  type Asset,
  type AssetScanResultWithMetadata,
  type AssetScanTypeNote,
  type AssetScanNote,
  ScanTypeName,
  AssetDiscoveryStatus,
} from '@/types/models/monitoring';
import useSearchQueryParams, { type SearchQueryParams } from '@/libs/search/useQueryParams';
import useFeatureFlags, { FeatureFlag } from '@/composables/feature-flags';
import api, {
  type AddAssetRequest,
  type AssetsRequest,
  type RejectedAsset,
  type FindingsRequest,
  type ScanResultsResponse,
  type CreateContextNoteVariables,
  type EditAssetRequest,
  type ScanResultsRequest,
} from './api';
import {
  ALLOWANCES_QUERY,
  EXTERNAL_MONITORING_ASSETS_QUERY,
  EXTERNAL_MONITORING_SCAN_RESULTS_QUERY,
  EXTERNAL_MONITORING_SUPPLIER_ASSETS_QUERY,
  EXTERNAL_MONITORING_SUPPLIER_SCAN_RESULTS_QUERY,
  EXTERNAL_MONITORING_FINDINGS_QUERY,
  EXTERNAL_MONITORING_SUPPLIER_FINDINGS_QUERY,
  EXTERNAL_MONITORING_SUPPLIER_FINDINGS_STATS_QUERY,
  EXTERNAL_MONITORING_FINDINGS_STATS_QUERY,
} from './composableKeys';
import type { AssetScanResultWithMetadataAndAsset } from './export';

export function useScanResults(
  params: MaybeRefOrGetter<ScanResultsRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EXTERNAL_MONITORING_SCAN_RESULTS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getScanResults(queryKey[1]);
        return res.data;
      },
    }),
  );

  const scanResults = computed(() => query.data.value?.scanResults ?? []);
  const totalScanResults = computed(() => query.data.value?.totalScanResults ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  const resultsByTypeID = computed(() => {
    const byType: Record<string, AssetScanResultWithMetadata[]> = {};
    for (const result of scanResults.value) {
      if (!byType[result.scanTypeID]) {
        byType[result.scanTypeID] = [];
      }

      byType[result.scanTypeID]!.push(result);
    }

    return byType;
  });

  return {
    ...query,
    query,
    scanResults,
    totalScanResults,
    page,
    resultsByTypeID,
  };
}

export function useSupplierScanResults(
  supplierID: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<ScanResultsRequest>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        EXTERNAL_MONITORING_SUPPLIER_SCAN_RESULTS_QUERY,
        toRef(supplierID),
        toRef(params),
      ] as const,
      async queryFn({ queryKey }) {
        const res = await api.getSupplierScanResults(queryKey[1], queryKey[2]);
        return res.data;
      },
    }),
  );

  const scanResults = computed(() => query.data.value?.scanResults ?? []);
  const totalScanResults = computed(() => query.data.value?.totalScanResults ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  const resultsByTypeID = computed(() => {
    const byType: Record<string, AssetScanResultWithMetadata[]> = {};
    for (const result of scanResults.value) {
      if (!byType[result.scanTypeID]) {
        byType[result.scanTypeID] = [];
      }

      byType[result.scanTypeID]!.push(result);
    }

    return byType;
  });

  return {
    ...query,
    query,
    scanResults,
    totalScanResults,
    page,
    resultsByTypeID,
  };
}

export function useSupplierScanResult(
  supplierID: MaybeRefOrGetter<string>,
  assetScanID: MaybeRefOrGetter<string>,
  includeDeletedAssets?: MaybeRefOrGetter<boolean>,
  assetStatuses?: MaybeRefOrGetter<AssetStatus[]>,
  options?: QueryOptions,
) {
  const query = useSupplierScanResults(
    supplierID,
    () => ({
      filter: {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'assetScanID',
            comparator: FilterComparator.Equal,
            value: toValue(assetScanID),
          },
        ],
      },
      includeDeletedAssets: toValue(includeDeletedAssets),
      assetStatuses: toValue(assetStatuses),
    }),
    options,
  );

  const scanResult = computed(() => query.scanResults.value[0]);

  return { ...query, query, scanResult };
}

export function useClientOrSupplierScanResults(
  type: MaybeRefOrGetter<'client' | 'supplier'>,
  orgID: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<ScanResultsRequest>,
  options?: QueryOptions,
) {
  const clientQuery = useScanResults(params, {
    ...options,
    enabled: () => toValue(type) === 'client' && toValue(toValue(options)?.enabled) !== false,
  });

  const supplierQuery = useSupplierScanResults(orgID, params, {
    ...options,
    enabled: () => toValue(type) === 'supplier' && toValue(toValue(options)?.enabled) !== false,
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );
  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const scanResults = computed(() =>
    toValue(type) === 'client' ? clientQuery.scanResults.value : supplierQuery.scanResults.value,
  );
  const resultsByAssetScanID = computed(() =>
    Object.fromEntries(scanResults.value.map((result) => [result.assetScanID, result])),
  );

  return { isLoading, isError, scanResults, resultsByAssetScanID };
}

export function useClientOrSupplierScanResult(
  type: MaybeRefOrGetter<'client' | 'supplier'>,
  orgID: MaybeRefOrGetter<string>,
  assetScanID: MaybeRefOrGetter<string>,
  includeDeletedAssets?: MaybeRefOrGetter<boolean | undefined>,
  includeAssetsWithStatus?: MaybeRefOrGetter<AssetStatus[] | undefined>,
  options?: QueryOptions,
) {
  const clientQuery = useClientOrSupplierScanResults(
    type,
    orgID,
    () => ({
      filter: {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'assetScanID',
            comparator: FilterComparator.Equal,
            value: toValue(assetScanID),
          },
        ],
      },
      includeDeletedAssets: toValue(includeDeletedAssets),
      assetStatuses: toValue(includeAssetsWithStatus),
    }),
    options,
  );

  const scanResult = computed(() => clientQuery.resultsByAssetScanID.value[toValue(assetScanID)]);

  return { ...clientQuery, scanResult };
}

export function useAssets(params: MaybeRefOrGetter<AssetsRequest>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getAssets(queryKey[1]);
        return res.data;
      },
    }),
  );

  const assets = computed(() => query.data.value?.assets || []);

  const activeAssets = computed(() =>
    assets.value.filter((asset) => asset.status === AssetStatus.Active),
  );
  const disabledAssets = computed(() =>
    assets.value.filter((asset) => asset.status === AssetStatus.Disabled),
  );
  const unverifiedAssets = computed(() =>
    assets.value.filter((asset) => asset.discoveryStatus === AssetDiscoveryStatus.Unverified),
  );
  const pendingReviewAssets = computed(() =>
    assets.value.filter((asset) => asset.discoveryStatus === AssetDiscoveryStatus.PendingReview),
  );

  const assetsByID = computed(() =>
    Object.fromEntries(assets.value.map((asset) => [asset.id, asset])),
  );

  const totalAssets = computed(() => query.data.value?.pagination.page.total ?? 0);
  const page = computed(() => query.data.value?.pagination.page ?? null);

  return {
    ...query,
    query,
    assets,
    activeAssets,
    disabledAssets,
    unverifiedAssets,
    pendingReviewAssets,
    assetsByID,
    totalAssets,
    page,
  };
}

export function useSupplierAssets(
  supplierID: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<AssetsRequest>,
  options?: QueryOptions,
) {
  const { ff } = useFeatureFlags();

  const filter = computed(() => {
    if (!ff(FeatureFlag.ExternalMonitoringDiscoveredAssets)) {
      return {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'discoveryStatus',
            comparator: FilterComparator.Equal,
            value: AssetDiscoveryStatus.Verified,
          },
          {
            field: 'status',
            comparator: FilterComparator.Equal,
            value: AssetStatus.Active,
          },
        ],
      };
    }
    return {
      operator: FilterOperator.And,
      operands: [
        {
          field: 'status',
          comparator: FilterComparator.Equal,
          value: AssetStatus.Active,
        },
      ],
    };
  });

  const computedParams = computed(() => {
    return {
      ...toValue(params),
      filter: toValue(params)?.filter || filter.value,
    };
  });
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        EXTERNAL_MONITORING_SUPPLIER_ASSETS_QUERY,
        toRef(supplierID),
        toRef(computedParams),
      ] as const,
      async queryFn({ queryKey }) {
        const res = await api.getSupplierAssets(queryKey[1], queryKey[2]);
        return res.data;
      },
    }),
  );

  const assets = computed(() => query.data.value?.assets || []);
  const activeAssets = computed(() =>
    assets.value.filter((asset) => asset.status === AssetStatus.Active),
  );
  const disabledAssets = computed(() =>
    assets.value.filter((asset) => asset.status === AssetStatus.Disabled),
  );
  const assetsByID = computed(() =>
    Object.fromEntries(assets.value.map((asset) => [asset.id, asset])),
  );
  const totalAssets = computed(() => query.data.value?.pagination.page.total ?? 0);
  const page = computed(() => query.data.value?.pagination.page ?? null);

  return { ...query, query, assets, activeAssets, disabledAssets, assetsByID, totalAssets, page };
}

export function useSupplierAsset(
  supplierID: MaybeRefOrGetter<string>,
  assetID: MaybeRefOrGetter<string>,
  includeDeletedAssets?: MaybeRefOrGetter<boolean> | undefined,
  options?: QueryOptions,
) {
  const query = useSupplierAssets(
    supplierID,
    () => ({
      filter: {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'id',
            comparator: FilterComparator.Equal,
            value: toValue(assetID),
          },
        ],
      },
      includeDeletedAssets: toValue(includeDeletedAssets),
    }),
    options,
  );

  const asset = computed(() => query.assetsByID.value[toValue(assetID)]);

  return { ...query, query, asset };
}

export function useClientOrSupplierAssets(
  type: MaybeRefOrGetter<'client' | 'supplier'>,
  orgID: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<AssetsRequest>,
  options?: QueryOptions,
) {
  const clientQuery = useAssets(params, {
    ...options,
    enabled: () => toValue(type) === 'client' && toValue(toValue(options)?.enabled) !== false,
  });
  const supplierQuery = useSupplierAssets(orgID, params, {
    ...options,
    enabled: () => toValue(type) === 'supplier' && toValue(toValue(options)?.enabled) !== false,
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );
  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const assets = computed(() =>
    toValue(type) === 'client' ? clientQuery.assets.value : supplierQuery.assets.value,
  );
  const assetsByID = computed(() =>
    toValue(type) === 'client' ? clientQuery.assetsByID.value : supplierQuery.assetsByID.value,
  );

  return { isLoading, isError, assets, assetsByID };
}

export function useClientOrSupplierAsset(
  type: MaybeRefOrGetter<'client' | 'supplier'>,
  orgID: MaybeRefOrGetter<string>,
  assetID: MaybeRefOrGetter<string>,
  includeDeletedAssets?: MaybeRefOrGetter<boolean> | undefined,
  options?: QueryOptions,
) {
  const clientQuery = useClientOrSupplierAssets(
    type,
    orgID,
    () => ({
      filter: {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'id',
            comparator: FilterComparator.Equal,
            value: toValue(assetID),
          },
        ],
      },
      includeDeletedAssets: toValue(includeDeletedAssets),
    }),
    options,
  );

  const asset = computed(() => clientQuery.assetsByID.value[toValue(assetID)]);

  return { ...clientQuery, asset };
}

export function useAddAsset(
  options?: MutationObserverOptions<Asset, AxiosError, { asset: AddAssetRequest }, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ asset }) {
        const resp = await api.addAsset(asset);
        return resp.data;
      },
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY] });
      },
    }),
  );
}

export function useEditAsset(
  options?: MutationObserverOptions<
    Asset,
    AxiosError,
    { asset: EditAssetRequest; assetId: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ asset, assetId }) {
        const resp = await api.editAsset(assetId, asset);
        return resp.data;
      },
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY] });
      },
    }),
  );
}

export function useRescanAsset(
  options?: MutationObserverOptions<
    Asset,
    AxiosError,
    { assetId: string; scanTypeId: string },
    null
  >,
) {
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ assetId, scanTypeId }) {
        const resp = await api.rescanAsset(assetId, scanTypeId);
        return resp.data;
      },
    }),
  );
}

export function useDeleteAsset(options?: MutationObserverOptions<null, AxiosError, string, null>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(id) {
        const resp = await api.deleteAsset(id);
        return resp.data;
      },
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY] });
      },
    }),
  );
}

export function useCreateContextNote(
  options?: MutationObserverOptions<
    AssetScanTypeNote,
    AxiosError,
    CreateContextNoteVariables,
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(vars) {
        const res = await api.createContextNote(vars);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueriesData(
          { queryKey: [EXTERNAL_MONITORING_SCAN_RESULTS_QUERY] },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.assetScanID === data.assetScanID ? { ...result, contextNote: data } : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useUpdateContextNote(
  options?: MutationObserverOptions<
    AssetScanTypeNote,
    AxiosError,
    { noteID: string; note: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ noteID, note }) {
        const res = await api.updateContextNote(noteID, note);
        return res.data;
      },
      onSuccess(data, variables) {
        queryClient.setQueriesData(
          { queryKey: [EXTERNAL_MONITORING_SCAN_RESULTS_QUERY] },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.contextNote?.id === variables.noteID
                  ? { ...result, contextNote: data }
                  : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useDeleteContextNote(
  options?: MutationObserverOptions<null, AxiosError, string, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(noteID) {
        const res = await api.deleteContextNote(noteID);
        return res.data;
      },
      onSuccess(_data, noteID) {
        queryClient.setQueriesData(
          { queryKey: [EXTERNAL_MONITORING_SCAN_RESULTS_QUERY] },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.contextNote?.id === noteID ? { ...result, contextNote: null } : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useCreateClientAssetScanNote(
  options?: MutationObserverOptions<
    AssetScanNote,
    AxiosError,
    { supplierID: string; assetScanID: string; note: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(vars) {
        const res = await api.createClientAssetScanNote(
          vars.supplierID,
          vars.assetScanID,
          vars.note,
        );
        return res.data;
      },
      onSuccess(data, vars) {
        queryClient.setQueriesData(
          {
            queryKey: [EXTERNAL_MONITORING_SUPPLIER_SCAN_RESULTS_QUERY, toRef(vars.supplierID)],
          },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.assetScanID === data.assetScanID
                  ? { ...result, assetScanNote: data }
                  : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useUpdateClientAssetScanNote(
  options?: MutationObserverOptions<
    AssetScanNote,
    AxiosError,
    { supplierID: string; noteID: string; note: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ noteID, note }) {
        const res = await api.updateClientAssetScanNote(noteID, note);
        return res.data;
      },
      onSuccess(data, variables) {
        queryClient.setQueriesData(
          {
            queryKey: [
              EXTERNAL_MONITORING_SUPPLIER_SCAN_RESULTS_QUERY,
              toRef(variables.supplierID),
            ],
          },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.assetScanNote?.id === variables.noteID
                  ? { ...result, assetScanNote: data }
                  : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useDeleteClientAssetScanNote(
  options?: MutationObserverOptions<
    AssetScanNote,
    AxiosError,
    {
      noteID: string;
      supplierID: string;
    },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(vars) {
        const res = await api.deleteClientAssetScanNote(vars.noteID);
        return res.data;
      },
      onSuccess(data, vars) {
        queryClient.setQueriesData(
          {
            queryKey: [EXTERNAL_MONITORING_SUPPLIER_SCAN_RESULTS_QUERY, toRef(vars.supplierID)],
          },
          (oldData: ScanResultsResponse | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              scanResults: oldData.scanResults.map((result) =>
                result.assetScanNote?.id === data.id ? { ...result, assetScanNote: null } : result,
              ),
            } satisfies ScanResultsResponse;
          },
        );
      },
    }),
  );
}

export function useAllowances(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ALLOWANCES_QUERY],
      async queryFn() {
        const res = await api.getAllowances();
        return res.data;
      },
    }),
  );

  const allowances = computed(() => query.data.value ?? []);
  const allowancesByType = computed(() =>
    Object.fromEntries(
      allowances.value.map((allowance) => [allowance.allowanceType, allowance.allowanceAmount]),
    ),
  );

  return {
    ...query,
    query,
    allowances,
    allowancesByType,
  };
}

export function transformFindingsFilterForBackend(
  filter: any,
  scanTypeByCategories: Record<string, string[]>,
) {
  if (!filter) return filter;
  if (!isGroupingFilterExpression(filter)) return filter;

  const operands = [...filter.operands];

  // Find scan_category operand
  const scanCategoryOperandIndex = operands.findIndex(
    (op) => isComparisonFilterExpression(op) && op.field === 'scan_category',
  );
  if (scanCategoryOperandIndex === -1) return filter;

  const scanCategoryOperand = operands[scanCategoryOperandIndex] as ComparisonFilterExpression;
  const scanCategoryComparator = scanCategoryOperand.comparator;
  const selectedCategories = (
    Array.isArray(scanCategoryOperand.value)
      ? scanCategoryOperand.value
      : [scanCategoryOperand.value]
  ).filter((x): x is string => typeof x === 'string');

  // Expand categories to scan types
  const scanTypes = selectedCategories
    .flatMap((cat) => scanTypeByCategories[cat as keyof typeof scanTypeByCategories] || [])
    .filter((x): x is ScanTypeName => typeof x === 'string') as ScanTypeName[];

  // Remove scan_category operand
  operands.splice(scanCategoryOperandIndex, 1);

  // Append scan_type operand with the same comparator and expanded values
  operands.push({
    id: crypto.randomUUID(),
    field: 'scan_type',
    comparator: scanCategoryComparator,
    value: scanTypes,
  });

  return {
    ...filter,
    operands,
  };
}

export function useScanTypeCategories() {
  const { t } = useI18n();

  const scanTypeByCategories = {
    portSecurity: [
      ScanTypeName.RemoteAccessPorts,
      ScanTypeName.DatabasePorts,
      ScanTypeName.WebServicePorts,
      ScanTypeName.FileSharingPorts,
      ScanTypeName.EmailPorts,
      ScanTypeName.InfrastructurePorts,
    ],
    emailSecurity: [ScanTypeName.SPF, ScanTypeName.DKIM, ScanTypeName.DMARC],
    webSecurity: [
      ScanTypeName.HSTS,
      ScanTypeName.CSP,
      ScanTypeName.SecurityHeaders,
      ScanTypeName.TLS,
    ],
    dnsSecurity: [ScanTypeName.DNS],
  };

  function getCategoryForScanType(scanType: ScanTypeName): string {
    for (const [categoryKey, scanTypes] of Object.entries(scanTypeByCategories)) {
      if (scanTypes.includes(scanType)) {
        return t(`externalMonitoring.categories.${categoryKey}`);
      }
    }

    // Fallback to scan type name if no category found
    return scanType;
  }

  /**
   * Get the raw category key for a given scan type (without translation)
   */
  function getCategoryKeyForScanType(scanType: ScanTypeName): string | null {
    for (const [categoryKey, scanTypes] of Object.entries(scanTypeByCategories)) {
      if (scanTypes.includes(scanType)) {
        return categoryKey;
      }
    }

    return null;
  }

  return {
    scanTypeByCategories,
    getCategoryForScanType,
    getCategoryKeyForScanType,
  };
}

export function useLatestScanResultFindings(
  params: MaybeRefOrGetter<FindingsRequest>,
  options?: QueryOptions,
) {
  const { scanTypeByCategories } = useScanTypeCategories();
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EXTERNAL_MONITORING_FINDINGS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        // Transform filter before sending to API
        const param = { ...queryKey[1] };
        if (param.filter) {
          param.filter = transformFindingsFilterForBackend(param.filter, scanTypeByCategories);
        }
        const res = await api.getLatestScanResultFindings(param);
        return res.data;
      },
    }),
  );

  const findings = computed(() => query.data.value?.findings ?? []);
  const page = computed(() => query.data.value?.page ?? null);

  return {
    ...query,
    query,
    findings,
    page,
  };
}

export function useSupplierLatestScanResultFindings(
  supplierID: MaybeRefOrGetter<string>,
  params: MaybeRefOrGetter<FindingsRequest>,
  options?: QueryOptions,
) {
  const { scanTypeByCategories } = useScanTypeCategories();
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        EXTERNAL_MONITORING_SUPPLIER_FINDINGS_QUERY,
        toRef(supplierID),
        toRef(params),
      ] as const,
      async queryFn({ queryKey }) {
        // Transform filter before sending to API
        const param = { ...queryKey[2] };
        if (param.filter) {
          param.filter = transformFindingsFilterForBackend(param.filter, scanTypeByCategories);
        }
        const res = await api.getLatestScanResultFindingsForSupplier(queryKey[1], param);
        return res.data;
      },
    }),
  );

  const findings = computed(() => query.data.value?.findings ?? []);
  const page = computed(() => query.data.value?.page ?? null);

  return {
    ...query,
    query,
    findings,
    page,
  };
}

// Adds the asset to each scan result
export function useScanResultsWithAsset(
  assets: MaybeRefOrGetter<Asset[]>,
  scanResults: MaybeRefOrGetter<AssetScanResultWithMetadata[]>,
): ComputedRef<AssetScanResultWithMetadataAndAsset[]> {
  return computed(() => {
    return toValue(scanResults).map((result) => {
      const asset = toValue(assets).find((asset) => asset.id === result.assetID);
      return { scanResult: result, asset };
    });
  });
}

// When mounted, if the element is the first element matching the selector, it returns true for expanded.
export function useExpandedIfFirstElementOfSelector(
  el: MaybeElementRef,
  selector: MaybeRefOrGetter<string>,
) {
  const isExpanded = ref(false);
  onMounted(() => {
    const selection = document.querySelector(`${toValue(selector)}`);
    if (selection === toValue(el)) {
      isExpanded.value = true;
    }
  });

  return isExpanded;
}

function searchQueryParamsLatestScanResultFindings(
  filter: GroupingFilterExpression | undefined,
): SearchQueryParams {
  return {
    filter,
    sort: [{ field: 'scanned_at', direction: SortDirection.Descending }],
    page: { limit: 25, offset: 0 },
  };
}

export function searchQueryParamsLatestScanResultFindingsForAsset(assetName?: string) {
  const filter = assetName
    ? {
        operator: FilterOperator.And,
        operands: [
          { field: 'asset_value', comparator: FilterComparator.IsAny, value: [assetName] },
        ],
      }
    : undefined;
  return searchQueryParamsLatestScanResultFindings(filter);
}

export function useSearchQueryParamsLatestScanResultFindings() {
  return useSearchQueryParams(searchQueryParamsLatestScanResultFindings(undefined));
}

export function useLatestScanResultFindingsStatsForSupplier(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EXTERNAL_MONITORING_SUPPLIER_FINDINGS_STATS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getLatestScanResultFindingsStatsForSupplier(queryKey[1]);
        return res.data;
      },
    }),
  );

  return {
    ...query,
    stats: query.data,
  };
}

export function useLatestScanResultFindingsStats(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [EXTERNAL_MONITORING_FINDINGS_STATS_QUERY],
      async queryFn() {
        const res = await api.getLatestScanResultFindingsStats();
        return res.data;
      },
    }),
  );

  return {
    ...query,
    stats: query.data,
  };
}

export function useIsPortScanType(scanType: MaybeRefOrGetter<string>) {
  return computed(() => {
    return toValue(scanType).endsWith('Ports');
  });
}

export function useVerifyAssets(
  options?: MutationObserverOptions<Asset[], AxiosError, { assetIds: string[] }, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ assetIds }) {
        const response = await api.verifyAssets(assetIds);
        return response.data;
      },
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY] });
      },
    }),
  );
}

export function useRejectAssets(
  options?: MutationObserverOptions<Asset[], AxiosError, { rejectedAssets: RejectedAsset[] }, null>,
) {
  const queryClient = useQueryClient();
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ rejectedAssets }) {
        const response = await api.rejectAssets(rejectedAssets);
        return response.data;
      },
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_MONITORING_ASSETS_QUERY] });
      },
    }),
  );
}
